import { Search, X } from "lucide-react";
import React from "react";

import Input from "./Input";
import "./input.css";

/**
 * SearchInput — design-system search field with leading icon and clear control.
 *
 * Built on Input so text size/styling stay on the body text token.
 *
 * @param {Object} props
 * @param {string} props.value - Controlled value
 * @param {Function} props.onChange - Change handler (receives the event)
 * @param {Function} [props.onClear] - Optional clear handler; defaults to clearing via a synthetic onChange
 * @param {string} [props.placeholder="Search"]
 * @param {string} [props.className] - Classes for the outer wrapper (e.g. width)
 * @param {string} [props.inputClassName] - Extra classes for the input element
 * @param {string} [props.background="white"] - Passed to Input ('default' | 'white')
 */
const SearchInput = React.forwardRef(({
    value = "",
    onChange,
    onClear,
    placeholder = "Search",
    className = "",
    inputClassName = "",
    background = "white",
    ...rest
}, ref) => {
    const hasValue = Boolean(value);

    const handleClear = () => {
        if (onClear) {
            onClear();
            return;
        }
        if (onChange) {
            onChange({ target: { value: "" } });
        }
    };

    return (
        <div className={`ui-search-input ${className}`.trim()}>
            <Input
                ref={ref}
                type="search"
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                background={background}
                className={`ui-search-input__field ${inputClassName}`.trim()}
                aria-label={rest["aria-label"] || placeholder}
                {...rest}
            />
            {hasValue ? (
                <button
                    type="button"
                    className="ui-search-input__icon ui-search-input__clear"
                    onClick={handleClear}
                    aria-label="Clear search"
                >
                    <X size="1em" aria-hidden />
                </button>
            ) : (
                <span className="ui-search-input__icon" aria-hidden>
                    <Search size="1em" />
                </span>
            )}
        </div>
    );
});

SearchInput.displayName = "SearchInput";

export default SearchInput;
